/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import {
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  styled,
  TextField,
  Toolbar,
  Typography,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import Box from "@mui/material/Box";
import Topheader from "../../../Layout/header/header";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Search } from "@mui/icons-material";

import ActiveTableData from "./tablularData/ActiveTableData";
import RequestsTableData from "./tablularData/RequestsTableData";
import DeactivatedTableData from "./tablularData/DeactivatedTableData";

import {
  fetchGlobalAdminUserDetails,
  fetchGlobalAdminUsersCount,
} from "../../../Services/redux/global-admin-users/GlobalAdminUsersActions";
import { useDispatch, useSelector } from "react-redux";

export const StyledHeaderCellTypo = styled(Typography)({
  fontWeight: 600,
  maxHeight: "16px",
  fontSize: "14px",
  lineHeight: "16px",
  letterSpacing: "0.4px",
  color: "#121415",
});

//Styled component for menu item starts

export const StyledMenuItem = styled(MenuItem)({
  height: "32px",
  minWidth: "156px",
  borderRadius: "4px",
  padding: "4px 8px",
  display: "flex",
  gap: "8px",
  alignItems: "center",
});

export const StyledTypography = styled(Typography)({
  width: "116px",
  height: "20px",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "20px",
  letterSpacing: "0.25px",
  verticalAlign: "middle",
});

//Styled component for header cell typography starts

export const StyledBodyCellTypo = styled(Typography)({
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "20px",
  letterSpacing: "0.25px",
  color: "#2A2C2D",
});

const Users = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedTab, setSelectedTab] = useState(0);
  const tabsStatus = useMemo(() => ["ACTIVE", "PENDING", "DEACTIVATED"], []);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showFullPageLoader, setShowFullPageLoader] = useState(true);

  const headerTitles = ["Active Users", "Signup Requests", "Deactivated Users"];

  const dispatch = useDispatch();

  const fetchActiveUsersList = useSelector(
    (state) => state.globalAdminUsers.activeUsersData
  );
  const fetchActiveUsersListCount = useSelector(
    (state) => state.globalAdminUsers.activeUsersDataCount
  );
  const fetchRequestedUsersList = useSelector(
    (state) => state.globalAdminUsers.requestUsersData
  );
  const fetchRequestedUsersListCount = useSelector(
    (state) => state.globalAdminUsers.requestUsersDataCount
  );
  const fetchDeactivatedUsersList = useSelector(
    (state) => state.globalAdminUsers.deactivatedUsersData
  );
  const fetchDeactivatedUsersListCount = useSelector(
    (state) => state.globalAdminUsers.deactivatedUsersDataCount
  );
  const isLoading = useSelector((state) => state.globalAdminUsers.loading);
  const countsLoading = useSelector(
    (state) => state.globalAdminUsers.countsLoading
  );

  const setPayload = useCallback(
    (val) => {
      const payload = {
        status: [tabsStatus[val]],
      };
      if (debouncedSearchTerm.trim() !== "") {
        payload.email = [debouncedSearchTerm.trim()];
      }
      return payload;
    },
    [debouncedSearchTerm, tabsStatus]
  );

  const handleStatusTabChange = (index) => {
    setShowFullPageLoader(true);
    setSelectedTab(index);
    const payloadValue = setPayload(index);
    dispatch(fetchGlobalAdminUserDetails(payloadValue));
  };

  // Debouncing effect for search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleEnterPress = (event) => {
    if (event.key === "Enter") {
      // Immediately trigger search on Enter, bypassing debounce delay
      setDebouncedSearchTerm(searchTerm);
    }
  };

  useEffect(() => {
    // Fetch counts first
    dispatch(fetchGlobalAdminUsersCount());
  }, [dispatch]);

  useEffect(() => {
    // Wait for counts to load before fetching tab data
    if (!countsLoading) {
      try {
        const payload = setPayload(selectedTab);
        // Fetch current tab data (blocking - shows loader)
        dispatch(fetchGlobalAdminUserDetails(payload));
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    }
  }, [dispatch, selectedTab, setPayload, debouncedSearchTerm, countsLoading]);

  useEffect(() => {
    if (!countsLoading && !isLoading && !isInitialLoad) {
      setShowFullPageLoader(false);
    }
  }, [countsLoading, isLoading, isInitialLoad]);

  useEffect(() => {
    if (isInitialLoad && !countsLoading && !isLoading) {
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [countsLoading, isLoading, isInitialLoad]);

  return (
    <>
      <Topheader />

      {showFullPageLoader && (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={showFullPageLoader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      {!showFullPageLoader && (
        <>
          <Toolbar />

          <Box sx={{ display: "flex" }}>
            <Box
              sx={{
                width: "240px",
                flexShrink: 0,
                paddingLeft: "75px",
                marginTop: "24px",
              }}
            >
              <List>
                {/* USERS Section Header */}
                <ListItem
                  sx={{
                    padding: "4px 16px",
                    borderRadius: "4px",
                    marginBottom: "4px",
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: "600",
                          color: "#5A5B5C",
                          textTransform: "uppercase",
                        }}
                      >
                        USERS
                      </Typography>
                    }
                  />
                </ListItem>

                {[
                  {
                    status: "Active",
                    text: "Active",
                    count: fetchActiveUsersListCount ?? 0,
                  },
                  {
                    status: "Requests",
                    text: "Requests",
                    count: fetchRequestedUsersListCount ?? 0,
                  },
                  {
                    status: "Deactivated",
                    text: "Deactivated",
                    count: fetchDeactivatedUsersListCount ?? 0,
                  },
                ].map((item) => (
                  <ListItem
                    key={item.status}
                    button
                    onClick={() => {
                      const statusMap = { Active: 0, Requests: 1 };
                      const statusIndex = statusMap[item.status] ?? 2;
                      handleStatusTabChange(statusIndex);
                    }}
                    sx={{
                      borderRadius: "4px",
                      bgcolor:
                        (item.status === "Active" && selectedTab === 0) ||
                        (item.status === "Requests" && selectedTab === 1) ||
                        (item.status === "Deactivated" && selectedTab === 2)
                          ? "#E5F3FF"
                          : "#ffffff",
                      padding: "2px",
                      cursor: "pointer",
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          fontSize="11px"
                          lineHeight="20px"
                          paddingLeft="16px !important"
                          fontWeight={
                            (item.status === "Active" && selectedTab === 0) ||
                            (item.status === "Requests" && selectedTab === 1) ||
                            (item.status === "Deactivated" && selectedTab === 2)
                              ? "400"
                              : "normal"
                          }
                          color={
                            (item.status === "Active" && selectedTab === 0) ||
                            (item.status === "Requests" && selectedTab === 1) ||
                            (item.status === "Deactivated" && selectedTab === 2)
                              ? "#0476E2"
                              : "#2A2C2D"
                          }
                          display="grid"
                          gridTemplateColumns="1fr auto"
                          alignItems="center"
                        >
                          <span
                            style={{
                              maxWidth: "200px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            {item.text}
                          </span>
                          <span
                            style={{
                              display: "flex",
                              minWidth: "25px",
                              maxWidth: "max-content",
                            }}
                          >
                            {item.count}
                          </span>
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}

              </List>
            </Box>
            <Box sx={{ flexGrow: 1, ml: 2, marginTop: "16px" }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
                width="97%"
              >
                <Typography sx={{ fontSize: "20px" }}>
                  {headerTitles[selectedTab]}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search for Email, Publisher & Role"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleEnterPress}
                    sx={{ width: 351 }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ width: "97%" }}>
                {selectedTab === 0 ? (
                  <ActiveTableData
                    activeData={fetchActiveUsersList.results}
                    searchTerm={debouncedSearchTerm}
                    currentTabStatus={tabsStatus[selectedTab]}
                    loading={false}
                  />
                ) : null}
                {selectedTab === 1 ? (
                  <RequestsTableData
                    searchTerm={debouncedSearchTerm}
                    requestsData={fetchRequestedUsersList.results}
                    loading={false}
                  />
                ) : null}
                {selectedTab === 2 ? (
                  <DeactivatedTableData
                    searchTerm={debouncedSearchTerm}
                    deactivatedData={fetchDeactivatedUsersList.results}
                    currentTabStatus={tabsStatus[selectedTab]}
                    loading={false}
                  />
                ) : null}
              </Box>
            </Box>
          </Box>
          <div>&nbsp;</div>
        </>
      )}
    </>
  );
};

export default Users;
